import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { billingApi } from "@/features/billing/api";
import { billingKeys } from "@/features/billing/query-keys";
import type { SubscriptionProduct } from "@/features/billing/types";

export function useBillingStatusQuery() {
  return useQuery({
    queryKey: billingKeys.status(),
    queryFn: billingApi.status,
  });
}

/**
 * Both mutations redirect the browser to Stripe on success — there's nothing to render after that.
 * onError is a no-op: SubscriptionSection shows the error inline (ErrorAlert), so the global toast
 * (see queryClient's default mutation onError) would be redundant.
 */
export function useCheckoutSessionMutation() {
  return useMutation({
    mutationFn: (product: SubscriptionProduct) => billingApi.checkoutSession(product),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => {},
  });
}

export function usePortalSessionMutation() {
  return useMutation({
    mutationFn: billingApi.portalSession,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: () => {},
  });
}

/**
 * Polls while the session is paid but the account's plan hasn't flipped to PAID yet — the
 * subscription webhook that actually grants the plan can land a moment after Stripe redirects
 * the browser back here.
 */
export function useCheckoutSessionStatusQuery(sessionId: string) {
  return useQuery({
    queryKey: billingKeys.checkoutSession(sessionId),
    queryFn: () => billingApi.checkoutSessionStatus(sessionId),
    retry: false,
    refetchInterval: (query) =>
      query.state.data?.complete && query.state.data.plan === "FREE" ? 2000 : false,
  });
}

/**
 * Fallback for when the browser never made it back to the checkout success page (closed tab,
 * failed redirect, crashed page) — looks the account up on Stripe directly instead of relying on
 * a Checkout Session id.
 */
export function useResyncBillingMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: billingApi.resync,
    onSuccess: (status) => {
      queryClient.setQueryData(billingKeys.status(), status);
    },
  });
}

export function useInvoicesQuery() {
  return useQuery({
    queryKey: billingKeys.invoices(),
    queryFn: billingApi.invoices,
  });
}
